import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Copy, CheckCircle2, Loader2, QrCode as QrCodeIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PaymentMethod } from '@/types';
import { CheckoutResponse, paymentService } from '@/lib/services/payment.service';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  checkoutData: CheckoutResponse | null;
  method: PaymentMethod;
  orderId: string;
  primaryDomain: string;
  onSuccess: () => void;
}

export function CheckoutModal({
  isOpen,
  onClose,
  checkoutData,
  method,
  orderId,
  primaryDomain,
  onSuccess,
}: CheckoutModalProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();
  const [pixStatus, setPixStatus] = useState<'waiting' | 'approved' | 'declined' | 'expired' | 'error'>('waiting');
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const attemptsRef = useRef(0);
  const errorsRef = useRef(0);

  // PIX expira em 10 minutos pelo MP — depois disso não adianta pollar.
  // PayPal: usuário pode demorar a logar/aprovar na outra tab → damos 30 min.
  const EXPIRATION_MS =
    method === PaymentMethod.PAYPAL ? 30 * 60 * 1000 : 10 * 60 * 1000;
  const POLL_INTERVAL_MS = 5000;
  const MAX_ATTEMPTS = Math.floor(EXPIRATION_MS / POLL_INTERVAL_MS);
  const MAX_CONSECUTIVE_ERRORS = 6; // ~30s de falha consecutiva → notifica

  // PIX precisa de polling (usuário paga no app do banco). PayPal também precisa:
  // o usuário aprova na tab do PayPal, o backend recebe o webhook e capture —
  // a tab do modal só sabe pelo polling. Cartão/Checkout Pro do MP tem o mesmo
  // padrão atual (sem polling aqui, redireciona).
  const isPollable = method === PaymentMethod.PIX || method === PaymentMethod.PAYPAL;

  useEffect(() => {
    if (!isOpen || !isPollable || !orderId || !primaryDomain) return;

    // Cleanup defensivo: garante que não existe interval anterior pendurado.
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    attemptsRef.current = 0;
    errorsRef.current = 0;
    setPixStatus('waiting');
    setConsecutiveErrors(0);

    let cancelled = false;

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const checkStatus = async () => {
      if (cancelled) return;
      try {
        const result = await paymentService.getPaymentStatusByOrderId(orderId, primaryDomain);
        errorsRef.current = 0;
        setConsecutiveErrors(0);
        const status = result.payment.status?.toUpperCase();

        if (status === 'APPROVED') {
          setPixStatus('approved');
          stopPolling();
          setTimeout(() => {
            if (cancelled) return;
            onSuccess();
            navigate(`/payment/success?payment_id=${result.payment.id}`);
          }, 1500);
          return;
        }

        // O prazo acabou e o pedido expirou: não adianta mais esperar.
        if (result.orderStatus === 'EXPIRED') {
          setPixStatus('expired');
          stopPolling();
          return;
        }

        if (status === 'DECLINED' || status === 'FAILED' || status === 'CANCELLED') {
          setPixStatus('declined');
          stopPolling();
          setTimeout(() => {
            if (cancelled) return;
            onClose();
            navigate(`/payment/failure?payment_id=${result.payment.id}`);
          }, 1500);
          return;
        }
      } catch (err) {
        errorsRef.current += 1;
        setConsecutiveErrors(errorsRef.current);
        // Erros transitórios (404 até webhook chegar) são esperados; só expõe o estado
        // se forem consecutivos — indica problema real de rede/servidor.
        if (errorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
          setPixStatus('error');
        }
        // eslint-disable-next-line no-console
        console.debug('[PIX poll] erro ao consultar status:', err);
      }

      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setPixStatus((prev) => (prev === 'waiting' || prev === 'error' ? 'expired' : prev));
        stopPolling();
      }
    };

    checkStatus();
    intervalRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [isOpen, method, orderId, primaryDomain]);

  if (!checkoutData) return null;

  const handleCopyPix = () => {
    if (checkoutData.qrCode) {
      navigator.clipboard.writeText(checkoutData.qrCode);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenCheckout = () => {
    if (checkoutData.checkoutUrl) {
      window.open(checkoutData.checkoutUrl, '_blank');
    }
  };

  const renderPixCheckout = () => (
    <div className="space-y-4">
      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-4">
          Escaneie o QR Code ou copie o código PIX para realizar o pagamento
        </p>
      </div>

      {/* QR Code */}
      {checkoutData.qrCodeBase64 ? (
        <div className="flex justify-center p-4 bg-white rounded-lg">
          <img
            src={checkoutData.qrCodeBase64}
            alt="QR Code PIX"
            className="w-64 h-64"
          />
        </div>
      ) : (
        <div className="flex justify-center p-8 bg-muted rounded-lg">
          <QrCodeIcon className="w-32 h-32 text-muted-foreground" />
        </div>
      )}

      {/* PIX Code */}
      {checkoutData.qrCode && (
        <div>
          <label className="text-sm font-medium mb-2 block">
            Código PIX (Copia e Cola)
          </label>
          <div className="flex gap-2">
            <div className="flex-1 p-3 bg-muted rounded-lg text-sm font-mono break-all">
              {checkoutData.qrCode}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopyPix}
              className="flex-shrink-0"
            >
              {copied ? (
                <CheckCircle2 className="w-4 h-4 text-success" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      )}

      {pixStatus === 'waiting' && (
        <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
          <p className="text-sm text-center text-muted-foreground">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
            Aguardando confirmação do pagamento...
          </p>
          <p className="text-xs text-center text-muted-foreground mt-2">
            Esta janela será atualizada automaticamente quando o pagamento for confirmado
          </p>
        </div>
      )}

      {pixStatus === 'approved' && (
        <div className="bg-success/10 border border-success/30 rounded-lg p-4">
          <p className="text-sm text-center text-success font-medium">
            <CheckCircle2 className="w-4 h-4 inline mr-2" />
            Pagamento confirmado! Redirecionando...
          </p>
        </div>
      )}

      {pixStatus === 'declined' && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
          <p className="text-sm text-center text-destructive font-medium">
            Pagamento recusado. Redirecionando...
          </p>
        </div>
      )}

      {pixStatus === 'expired' && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
          <p className="text-sm text-center text-destructive font-medium">
            {t('store.cart.order_expired')}
          </p>
        </div>
      )}

      {pixStatus === 'error' && (
        <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
          <p className="text-sm text-center text-warning font-medium">
            Não conseguimos verificar o status do pagamento ({consecutiveErrors} falhas consecutivas).
            Se você já pagou, aguarde alguns minutos — o sistema se recupera automaticamente.
          </p>
        </div>
      )}
    </div>
  );

  const renderPaypalCheckout = () => (
    <div className="space-y-4">
      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-4">
          Você será redirecionado para o PayPal para completar o pagamento com segurança.
        </p>
      </div>

      <div className="flex justify-center">
        <Button
          onClick={handleOpenCheckout}
          size="lg"
          className="w-full max-w-sm bg-[#0070ba] hover:bg-[#005a96] text-white"
          disabled={!checkoutData.checkoutUrl}
        >
          Pagar com PayPal
        </Button>
      </div>

      <div className="bg-muted/50 rounded-lg p-4">
        <p className="text-xs text-center text-muted-foreground">
          Ao clicar acima, uma nova aba se abre no PayPal. Após aprovar o pagamento,
          volte para esta janela — ela será atualizada automaticamente.
        </p>
      </div>

      {pixStatus === 'waiting' && (
        <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
          <p className="text-sm text-center text-muted-foreground">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
            Aguardando confirmação do pagamento...
          </p>
        </div>
      )}

      {pixStatus === 'approved' && (
        <div className="bg-success/10 border border-success/30 rounded-lg p-4">
          <p className="text-sm text-center text-success font-medium">
            <CheckCircle2 className="w-4 h-4 inline mr-2" />
            Pagamento confirmado! Redirecionando...
          </p>
        </div>
      )}

      {pixStatus === 'declined' && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
          <p className="text-sm text-center text-destructive font-medium">
            Pagamento recusado ou cancelado. Redirecionando...
          </p>
        </div>
      )}

      {pixStatus === 'expired' && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
          <p className="text-sm text-center text-destructive font-medium">
            {t('store.cart.order_expired')}
          </p>
        </div>
      )}

      {pixStatus === 'error' && (
        <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
          <p className="text-sm text-center text-warning font-medium">
            Não conseguimos verificar o status ({consecutiveErrors} falhas consecutivas).
            Se você já pagou, aguarde alguns minutos — o sistema se recupera automaticamente.
          </p>
        </div>
      )}
    </div>
  );

  const renderCardCheckout = () => (
    <div className="space-y-4">
      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-4">
          Você será redirecionado para completar o pagamento com cartão
        </p>
      </div>

      <div className="flex justify-center">
        <Button
          onClick={handleOpenCheckout}
          size="lg"
          className="w-full max-w-sm"
        >
          Ir para Pagamento
        </Button>
      </div>

      <div className="bg-muted/50 rounded-lg p-4">
        <p className="text-xs text-center text-muted-foreground">
          Você será redirecionado para o ambiente seguro do Mercado Pago para
          inserir os dados do cartão
        </p>
      </div>
    </div>
  );

  const getTitle = () => {
    switch (method) {
      case PaymentMethod.PIX:
        return 'Pagamento via PIX';
      case PaymentMethod.CREDIT_CARD:
        return 'Pagamento com Cartão de Crédito';
      case PaymentMethod.DEBIT_CARD:
        return 'Pagamento com Cartão de Débito';
      case PaymentMethod.PAYPAL:
        return 'Pagamento via PayPal';
      default:
        return 'Finalizar Pagamento';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>
            Complete o pagamento para finalizar sua compra
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {method === PaymentMethod.PIX && renderPixCheckout()}
          {method === PaymentMethod.PAYPAL && renderPaypalCheckout()}
          {(method === PaymentMethod.CREDIT_CARD ||
            method === PaymentMethod.DEBIT_CARD) &&
            renderCardCheckout()}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <div>
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-lg font-bold">
              {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(checkoutData.amount / 100)}
            </p>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
